import React, {
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  FormEvent,
} from "react";
import { useTranslation } from "react-i18next";
import { User, Edit, Trash2, Plus, Eye } from "lucide-react";
import { recruitingAPI } from "../services/recruitingApi";
import SearchInput from "../components/SearchInput";
import type { UserRole } from "../types/shared.types";
import "./UserManagement.css";

interface User {
  id: number;
  username: string;
  email: string;
  role: string;
  is_active: boolean;
  created_at?: string;
}

interface FormData {
  username: string;
  email: string;
  password: string;
  role: string;
  isActive: boolean;
}

interface FormErrors {
  general?: string;
  [key: string]: string | undefined;
}

interface AdminUsersResponse {
  users?: User[];
}

interface SystemConfigResponse {
  data?: {
    value?: string;
  };
}

const UserManagement: React.FC = () => {
  const { t } = useTranslation(["admin", "common"]);
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filterRole, setFilterRole] = useState<string>("all");
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [formData, setFormData] = useState<FormData>({
    username: "",
    email: "",
    password: "",
    role: "recruiter",
    isActive: true,
  });
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [viewAccessMode, setViewAccessMode] = useState<string>("all_open");
  const [isUpdatingViewMode, setIsUpdatingViewMode] = useState<boolean>(false);

  useEffect(() => {
    loadUsers();
    loadViewMode();
  }, []);

  const filterUsers = useCallback(() => {
    let filtered = users;
    if (searchTerm) {
      filtered = filtered.filter(
        (user) =>
          user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }
    if (filterRole !== "all") {
      filtered = filtered.filter((user) => user.role === filterRole);
    }
    setFilteredUsers(filtered);
  }, [users, searchTerm, filterRole]);

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const loadUsers = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const response =
        (await recruitingAPI.getAdminUsers()) as AdminUsersResponse;
      setUsers(response.users || []);
    } catch (error) {
      console.error("Error loading users:", error);
      alert(t("userManagement.loadError"));
    } finally {
      setIsLoading(false);
    }
  };

  const loadViewMode = async (): Promise<void> => {
    try {
      const response = (await recruitingAPI.getSystemConfig(
        "view_access_mode",
      )) as SystemConfigResponse;
      const mode = response.data?.value || "all_open";
      setViewAccessMode(mode);
    } catch (error) {
      console.error("Error loading view mode:", error);
    }
  };

  const handleViewModeChange = async (mode: string): Promise<void> => {
    if (mode === viewAccessMode) return;
    setIsUpdatingViewMode(true);
    try {
      await recruitingAPI.setSystemConfig("view_access_mode", mode);
      setViewAccessMode(mode);
    } catch (error) {
      console.error("Error updating view mode:", error);
      alert(t("userManagement.viewModeUpdateError"));
    } finally {
      setIsUpdatingViewMode(false);
    }
  };

  const handleCreateUser = async (
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.email) {
      setFormErrors({ general: t("userManagement.requiredFieldsError") });
      return;
    }

    setIsLoading(true);
    setFormErrors({});

    try {
      await recruitingAPI.createUser({
        username: formData.username,
        password: formData.password,
        email: formData.email,
        role: formData.role as UserRole,
      });

      await loadUsers();
      setShowCreateModal(false);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "recruiter",
        isActive: true,
      });
    } catch (error) {
      console.error("Error creating user:", error);
      const apiError = error as {
        response?: { data?: { error?: string; message?: string } };
      };
      setFormErrors({
        general:
          apiError.response?.data?.error ||
          apiError.response?.data?.message ||
          t("userManagement.createError"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditUser = async (
    e: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    e.preventDefault();
    if (!selectedUser) return;

    setIsLoading(true);
    setFormErrors({});

    try {
      await recruitingAPI.updateUser(selectedUser.id, {
        email: formData.email,
        role: formData.role as UserRole,
        is_active: formData.isActive,
      });

      await loadUsers();
      setShowEditModal(false);
      setSelectedUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "recruiter",
        isActive: true,
      });
    } catch (error) {
      console.error("Error updating user:", error);
      const apiError = error as {
        response?: { data?: { error?: string; message?: string } };
      };
      setFormErrors({
        general:
          apiError.response?.data?.error ||
          apiError.response?.data?.message ||
          t("userManagement.updateError"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (
    userId: number,
    username: string,
  ): Promise<void> => {
    if (!window.confirm(t("userManagement.confirmDelete", { username })))
      return;

    setIsLoading(true);
    try {
      await recruitingAPI.deleteUser(userId);
      await loadUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      alert(t("userManagement.deleteError"));
    } finally {
      setIsLoading(false);
    }
  };

  const openEditModal = (user: User): void => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      email: user.email,
      password: "",
      role: user.role,
      isActive: user.is_active,
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  const openCreateModal = (): void => {
    setFormData({
      username: "",
      email: "",
      password: "",
      role: "recruiter",
      isActive: true,
    });
    setFormErrors({});
    setShowCreateModal(true);
  };

  if (isLoading && users.length === 0) {
    return (
      <div className="user-management-loading">
        <div className="spinner"></div>
        <p>{t("userManagement.loading")}</p>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>{t("userManagement.title")}</h1>
        <button className="btn-primary" onClick={openCreateModal}>
          <Plus size={16} /> {t("userManagement.addUser")}
        </button>
      </div>

      <div className="view-mode-toggle">
        <span>{t("userManagement.globalViewMode")}:</span>
        <div className="view-mode-buttons">
          <button
            className={`view-mode-btn ${viewAccessMode === "all_open" ? "active" : ""}`}
            onClick={() => handleViewModeChange("all_open")}
            disabled={isUpdatingViewMode}
          >
            {t("userManagement.allOpen")}
          </button>
          <button
            className={`view-mode-btn ${viewAccessMode === "locked" ? "active" : ""}`}
            onClick={() => handleViewModeChange("locked")}
            disabled={isUpdatingViewMode}
          >
            {t("userManagement.locked")}
          </button>
        </div>
        <small className="view-mode-note">
          {viewAccessMode === "locked"
            ? t("userManagement.lockedDescription")
            : t("userManagement.allOpenDescription")}
        </small>
      </div>

      <div className="filters-section">
        <div className="search-box">
          <SearchInput
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder={t("userManagement.searchPlaceholder")}
            ariaLabel={t("userManagement.searchUsers")}
            showSearchIcon
            showClearButton
          />
        </div>

        <div className="filter-controls">
          <select
            value={filterRole}
            onChange={(e: ChangeEvent<HTMLSelectElement>) =>
              setFilterRole(e.target.value)
            }
            className="filter-select"
          >
            <option value="all">{t("userManagement.roles.all")}</option>
            <option value="application_admin">
              {t("userManagement.roles.applicationAdmin")}
            </option>
            <option value="organization_admin">
              {t("userManagement.roles.organizationAdmin")}
            </option>
            <option value="head_of_recruitment">
              {t("userManagement.roles.headOfRecruitment")}
            </option>
            <option value="head_of_sales">
              {t("userManagement.roles.headOfSales")}
            </option>
            <option value="recruiter">
              {t("userManagement.roles.recruiter")}
            </option>
            <option value="sales">{t("userManagement.roles.sales")}</option>
            <option value="tech_checker">
              {t("userManagement.roles.techChecker")}
            </option>
          </select>
        </div>
      </div>

      <div className="users-table-container">
        <table className="users-table">
          <thead>
            <tr>
              <th>{t("userManagement.table.user")}</th>
              <th>{t("userManagement.table.email")}</th>
              <th>{t("userManagement.table.role")}</th>
              <th>{t("common:status")}</th>
              <th>{t("userManagement.table.created")}</th>
              <th>{t("userManagement.table.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map((user) => (
              <tr key={user.id}>
                <td>
                  <div className="user-info">
                    <div className="user-avatar">
                      <User size={16} />
                    </div>
                    <div>
                      <div className="user-name">{user.username}</div>
                      <div className="user-username">@{user.username}</div>
                    </div>
                  </div>
                </td>
                <td>{user.email}</td>
                <td>
                  <span className={`role-badge role-${user.role}`}>
                    {user.role}
                  </span>
                </td>
                <td>
                  <span
                    className={`status-badge status-${user.is_active ? "active" : "inactive"}`}
                  >
                    {user.is_active ? t("common:active") : t("common:inactive")}
                  </span>
                </td>
                <td>
                  {user.created_at
                    ? new Date(user.created_at).toLocaleDateString()
                    : "-"}
                </td>
                <td>
                  <div className="action-buttons">
                    <button
                      className="btn-secondary btn-sm"
                      onClick={() => openEditModal(user)}
                      title={t("userManagement.editUser")}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-secondary btn-sm"
                      title={t("userManagement.viewDetails")}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="btn-secondary btn-sm delete-btn"
                      onClick={() => handleDeleteUser(user.id, user.username)}
                      title={t("userManagement.deleteUser")}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{t("userManagement.createNewUser")}</h3>
              <button
                className="modal-close"
                onClick={() => setShowCreateModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              {formErrors.general && (
                <div className="error-message general-error">
                  {formErrors.general}
                </div>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="create-username">
                  {t("userManagement.form.username")}
                </label>
                <input
                  type="text"
                  id="create-username"
                  name="username"
                  value={formData.username}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, username: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="create-email">
                  {t("userManagement.form.email")}
                </label>
                <input
                  type="email"
                  id="create-email"
                  name="email"
                  value={formData.email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="create-password">
                  {t("userManagement.form.password")}
                </label>
                <input
                  type="password"
                  id="create-password"
                  name="password"
                  value={formData.password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="create-role">
                    {t("userManagement.form.role")}
                  </label>
                  <select
                    id="create-role"
                    name="role"
                    value={formData.role}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="form-input"
                  >
                    <option value="recruiter">
                      {t("userManagement.roles.recruiter")}
                    </option>
                    <option value="sales">
                      {t("userManagement.roles.sales")}
                    </option>
                    <option value="head_of_recruitment">
                      {t("userManagement.roles.headOfRecruitment")}
                    </option>
                    <option value="head_of_sales">
                      {t("userManagement.roles.headOfSales")}
                    </option>
                    <option value="tech_checker">
                      {t("userManagement.roles.techChecker")}
                    </option>
                    <option value="organization_admin">
                      {t("userManagement.roles.organizationAdmin")}
                    </option>
                    <option value="application_admin">
                      {t("userManagement.roles.applicationAdmin")}
                    </option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="create-status">
                    {t("common:status")}
                  </label>
                  <select
                    id="create-status"
                    name="isActive"
                    value={String(formData.isActive)}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setFormData({
                        ...formData,
                        isActive: e.target.value === "true",
                      })
                    }
                    className="form-input"
                  >
                    <option value="true">{t("common:active")}</option>
                    <option value="false">{t("common:inactive")}</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowCreateModal(false)}
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t("userManagement.creating")
                    : t("userManagement.createUser")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditModal && selectedUser && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{t("userManagement.editUserTitle")}</h3>
              <button
                className="modal-close"
                onClick={() => setShowEditModal(false)}
              >
                ×
              </button>
            </div>
            <form onSubmit={handleEditUser}>
              {formErrors.general && (
                <div className="error-message general-error">
                  {formErrors.general}
                </div>
              )}
              <div className="form-group">
                <label className="form-label" htmlFor="edit-username">
                  {t("userManagement.form.username")}
                </label>
                <input
                  type="text"
                  id="edit-username"
                  name="username"
                  value={formData.username}
                  className="form-input"
                  disabled
                />
              </div>
              <div className="form-group">
                <label className="form-label" htmlFor="edit-email">
                  {t("userManagement.form.email")}
                </label>
                <input
                  type="email"
                  id="edit-email"
                  name="email"
                  value={formData.email}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="form-input"
                  required
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-role">
                    {t("userManagement.form.role")}
                  </label>
                  <select
                    id="edit-role"
                    name="role"
                    value={formData.role}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setFormData({ ...formData, role: e.target.value })
                    }
                    className="form-input"
                  >
                    <option value="recruiter">
                      {t("userManagement.roles.recruiter")}
                    </option>
                    <option value="sales">
                      {t("userManagement.roles.sales")}
                    </option>
                    <option value="head_of_recruitment">
                      {t("userManagement.roles.headOfRecruitment")}
                    </option>
                    <option value="head_of_sales">
                      {t("userManagement.roles.headOfSales")}
                    </option>
                    <option value="tech_checker">
                      {t("userManagement.roles.techChecker")}
                    </option>
                    <option value="organization_admin">
                      {t("userManagement.roles.organizationAdmin")}
                    </option>
                    <option value="application_admin">
                      {t("userManagement.roles.applicationAdmin")}
                    </option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label" htmlFor="edit-status">
                    {t("common:status")}
                  </label>
                  <select
                    id="edit-status"
                    name="isActive"
                    value={String(formData.isActive)}
                    onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                      setFormData({
                        ...formData,
                        isActive: e.target.value === "true",
                      })
                    }
                    className="form-input"
                  >
                    <option value="true">{t("common:active")}</option>
                    <option value="false">{t("common:inactive")}</option>
                  </select>
                </div>
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowEditModal(false)}
                >
                  {t("common:cancel")}
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={isLoading}
                >
                  {isLoading
                    ? t("userManagement.saving")
                    : t("userManagement.saveChanges")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
